import { useState, useEffect, useRef } from 'react';
import { GeocodingService } from '../services/geocoding.service';
import { GeocodingCache } from '../services/geocoding-cache.service';

interface UseGeocodingOptions {
  priority?: number;
  fallback?: string;
  skipCache?: boolean;
}

interface UseGeocodingResult {
  address: string;
  loading: boolean;
  error: string | null;
  isFromCache: boolean;
  refetch: () => void;
}

/**
 * Hook for geocoding with caching and optimizations
 */
export function useGeocoding(
  latitude?: number | null,
  longitude?: number | null,
  options: UseGeocodingOptions = {}
): UseGeocodingResult {
  const { priority = 0, fallback = 'Coordonnées non disponibles', skipCache = false } = options;
  
  const [address, setAddress] = useState<string>(fallback);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  
  // Keep track of the current request to avoid race conditions
  const requestIdRef = useRef(0);

  const fetchAddress = async () => {
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      setAddress(fallback);
      setLoading(false);
      setError('Coordonnées invalides');
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    
    setLoading(true);
    setError(null);
    setIsFromCache(false);

    try {
      // Check cache first (unless skipCache is true)
      if (!skipCache) {
        const cached = GeocodingCache.get(latitude, longitude);
        if (cached) {
          // Only update if this is still the current request
          if (currentRequestId === requestIdRef.current) {
            setAddress(cached);
            setIsFromCache(true);
            setLoading(false);
          }
          return;
        }
      }

      // Get address from service (which includes its own caching)
      const geocodingService = GeocodingService.getInstance();
      const result = await geocodingService.getAddress(latitude, longitude, priority);
      
      // Only update if this is still the current request
      if (currentRequestId === requestIdRef.current) {
        setAddress(result);
        setLoading(false);
      }
    } catch (err) {
      // Only update if this is still the current request
      if (currentRequestId === requestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Erreur de géocodage');
        setAddress(fallback);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchAddress();
    
    // Cleanup function to mark any pending request as stale
    return () => {
      requestIdRef.current++;
    };
  }, [latitude, longitude, priority, skipCache]);

  const refetch = () => {
    fetchAddress();
  };

  return {
    address,
    loading,
    error,
    isFromCache,
    refetch
  };
}